import { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, Users, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n/i18n";

const VERDICT = {
  interesting: { label: "Interesante", icon: ThumbsUp, on: "border-emerald-600 bg-emerald-600 text-white", tone: "text-emerald-700" },
  not: { label: "No encaja", icon: ThumbsDown, on: "border-slate-900 bg-slate-900 text-white", tone: "text-slate-600" },
};

/** Compact counters shown in the candidates table. */
export const FeedbackBadge = ({ fb, testId }) => {
  if (!fb || !(fb.interesting || fb.not || fb.comments)) return null;
  return (
    <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500" data-testid={testId}>
      {fb.interesting > 0 && <span className="flex items-center gap-0.5 text-emerald-700"><ThumbsUp className="h-3 w-3" />{fb.interesting}</span>}
      {fb.not > 0 && <span className="flex items-center gap-0.5"><ThumbsDown className="h-3 w-3" />{fb.not}</span>}
    </span>
  );
};

/** Colleague opinions from shared links, inside the candidate sheet. */
export const ColleagueFeedback = ({ pid }) => {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/company/candidates/${pid}/feedback`).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [pid]);
  if (!items.length) return null;
  return (
    <div data-testid="candidate-colleague-feedback">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-400"><Users className="h-3.5 w-3.5" /> {t("Opinión de colegas")}</p>
      <ul className="mt-3 space-y-2">
        {items.map((f) => {
          const v = VERDICT[f.verdict];
          return (
            <li key={f.id} className="rounded-2xl border border-slate-200 p-4 text-sm" data-testid={`colleague-feedback-${f.id}`}>
              <p className="flex items-center gap-2 font-semibold">{f.reviewerName}{v && <span className={`flex items-center gap-1 text-xs ${v.tone}`}><v.icon className="h-3.5 w-3.5" /> {t(v.label)}</span>}</p>
              {f.comment && <p className="mt-1 whitespace-pre-line text-slate-700">{f.comment}</p>}
              <p className="mt-1 text-[11px] text-slate-400">{f.shareLabel || t("Candidatos favoritos")} · {new Date(f.updatedAt).toLocaleString(lang, { dateStyle: "short", timeStyle: "short" })}</p>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** Controls on the public shared page. */
export const ReviewControls = ({ token, pid, reviewer, initial }) => {
  const { t } = useI18n();
  const [verdict, setVerdict] = useState(initial?.verdict || null);
  const [comment, setComment] = useState(initial?.comment || "");
  const [saved, setSaved] = useState(initial?.comment || "");
  const [busy, setBusy] = useState(false);
  const save = async (v, c) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/shared/${token}/feedback/${pid}`, { reviewerId: reviewer.id, reviewerName: reviewer.name, verdict: v, comment: c });
      setVerdict(data.verdict); setSaved(data.comment); setComment(data.comment);
      return true;
    } catch (e) { toast.error(t(apiError(e).message)); return false; } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`review-${pid}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">{t("Tu opinión")}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {Object.entries(VERDICT).map(([k, v]) => (
          <button key={k} onClick={() => save(verdict === k ? null : k, saved)} disabled={busy} aria-pressed={verdict === k} className={`flex h-10 items-center gap-2 rounded-xl border px-4 text-xs font-bold transition-colors disabled:opacity-60 ${verdict === k ? v.on : "border-slate-200 hover:border-slate-900"}`} data-testid={`review-${k}-${pid}`}>
            <v.icon className="h-3.5 w-3.5" /> {t(v.label)}
          </button>
        ))}
      </div>
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={300} placeholder={t("Comentario opcional para el equipo…")} className="mt-3 min-h-[64px] rounded-xl text-sm" data-testid={`review-comment-${pid}`} />
      {comment.trim() !== saved && (
        <button onClick={async () => { if (await save(verdict, comment)) toast.success(t("Comentario guardado")); }} disabled={busy} className="mt-2 flex h-9 items-center gap-2 rounded-lg bg-slate-950 px-4 text-xs font-bold text-white disabled:opacity-60" data-testid={`review-save-${pid}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} {t("Guardar comentario")}
        </button>
      )}
    </div>
  );
};

const KEY = "upv_reviewer";

export function useReviewer() {
  const [r, setR] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(KEY)); if (v?.id) return v; } catch { /* noop */ }
    const v = { id: (crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`).slice(0, 64), name: "" };
    localStorage.setItem(KEY, JSON.stringify(v));
    return v;
  });
  const setName = (name) => { const v = { ...r, name: name.slice(0, 60) }; setR(v); localStorage.setItem(KEY, JSON.stringify(v)); };
  return [r, setName];
}
